import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCalendarAlt, FaClock, FaUser, FaTag, FaEye, FaHeart, FaShare } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Blogs = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  // Fetch blogs from API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get('https://api.marketmaestroovip.in/api/blogs');
        setBlogPosts(res.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate read time based on content length
  const calculateReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  // Strip HTML for excerpt
  const stripHtmlTags = (html, length = 120) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Technology': 'bg-purple-100 text-purple-800 border-purple-200',
      'Security': 'bg-red-100 text-red-800 border-red-200',
      'Privacy': 'bg-green-100 text-green-800 border-green-200',
      'Guides': 'bg-blue-100 text-blue-800 border-blue-200',
      'News': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Tutorial': 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Maestroo Portfolio Dashboard</h1>
                <p className="text-blue-100">Comprehensive portfolio analysis and research reports</p>
                <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
              </div>
            </div>
          </div>
        </div>
        <div className="min-h-screen bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Maestroo Portfolio Dashboard</h1>
                  <p className="text-blue-100">Comprehensive portfolio analysis and research reports</p>
                  <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
                </div>
              </div>
            </div>
          </div>
          <div className="min-h-screen bg-gray-50 py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                <p className="text-red-600 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition shadow-md hover:shadow-lg"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        <Footer />
      </>
    );
  }

  return (
    <>
        <Header />
        
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <div>
                      <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Maestroo Portfolio Dashboard</h1>
                      <p className="text-blue-100">Comprehensive portfolio analysis and research reports</p>
                      <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
                    </div>
                  </div>
              </div>
          </div>

          {/* Blog Section */}
          <section className="min-h-screen py-16 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  
                  {/* Section Header */}
                  <div className="text-center mb-12">
                      <motion.h2 
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                      >
                          Latest Insights & Updates
                      </motion.h2>
                      <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="text-gray-600 max-w-2xl mx-auto"
                      >
                          Stay informed with the latest news, guides, and insights from our experts
                      </motion.p>
                  </div>

                  {/* Blog Grid */}
                  {blogPosts.length === 0 ? (
                      <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                          <div className="text-gray-400 text-lg">No blog posts available yet.</div>
                          <p className="text-gray-500 mt-2">Check back soon for new content!</p>
                      </div>
                  ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {blogPosts.map((post, index) => (
                              <motion.article
                                  key={post.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  whileInView={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.1 }}
                                  viewport={{ once: true }}
                                  onHoverStart={() => setHoveredId(post.id)}
                                  onHoverEnd={() => setHoveredId(null)}
                                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                              >
                                  {/* Thumbnail Container with Overlay */}
                                  <Link to={`/blog/${post.id}`} className="block relative h-52 overflow-hidden">
                                      {post.image ? (
                                          <>
                                              <img 
                                                  src={`https://api.marketmaestroovip.in/${post.image}`}
                                                  alt={post.title}
                                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                  onError={(e) => {
                                                      e.target.src = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800';
                                                  }}
                                              />
                                              {/* Gradient Overlay */}
                                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                          </>
                                      ) : (
                                          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                                              <span className="text-gray-400">No image</span>
                                          </div>
                                      )}
                                      
                                      {/* Category Badge */}
                                      {post.category && (
                                          <div className={`absolute top-4 left-4 ${getCategoryColor(post.category)} text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border shadow-sm z-10`}>
                                              {post.category}
                                          </div>
                                      )}

                                      {/* Reading Time Badge */}
                                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1 z-10">
                                          <FaClock className="text-blue-600 text-xs" />
                                          {calculateReadTime(post.content)}
                                      </div>
                                  </Link>

                                  {/* Content Container */}
                                  <div className="p-6">
                                      {/* Meta Info */}
                                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                          <span className="flex items-center gap-1.5">
                                              <FaCalendarAlt className="text-blue-600" /> 
                                              {formatDate(post.created_at)}
                                          </span>
                                          <span className="flex items-center gap-1.5">
                                              <FaUser className="text-blue-600" /> 
                                              Admin
                                          </span>
                                      </div>
                                      
                                      {/* Title */}
                                      <Link to={`/blog/${post.id}`}>
                                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors line-clamp-2">
                                              {post.title}
                                          </h3>
                                      </Link>
                                      
                                      {/* Excerpt */}
                                      <p className="text-gray-600 text-sm mb-5 line-clamp-3 leading-relaxed">
                                          {stripHtmlTags(post.content, 120)}
                                      </p>

                                      {/* Footer */}
                                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                          {/* Read More Link */}
                                          <Link 
                                              to={`/blog/${post.id}`}
                                              className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-800 transition-colors group/link"
                                          >
                                              Read Article 
                                              <FaArrowRight className="text-sm group-hover/link:translate-x-1.5 transition-transform" />
                                          </Link>

                                      </div>
                                  </div>

                                  {/* Hover Indicator */}
                                  {/* <div className={`h-1 bg-gradient-to-r from-blue-600 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div> */}
                              </motion.article>
                          ))}
                      </div>
                  )}

              </div>
          </section>

        <Footer />
    </>
  );
};

export default Blogs;